import type { SearchFileMatch } from '../store/search.ts'
import { createEffect, createMemo, createSignal, For, Index, Match, on, Show, Switch } from 'solid-js'
import { clearSearchScope, searchQuery, searchScope, useSearchResults } from '../store/search.ts'
import { useShortcuts } from '../store/shortcuts.ts'
import { openFile } from '../store/view.ts'
import { copy } from '../utils/clipboard.ts'
import { cn } from '../utils/cn.ts'
import { formatSize } from '../utils/format.ts'
import { CodeLine, lineTokens } from './CodeLine.tsx'
import { FacetsPanel } from './FacetsPanel.tsx'
import { Button } from './ui/Button.tsx'

const COLLAPSED_MATCHES = 8

function FileResult(props: { result: SearchFileMatch, selected: boolean }) {
  const [expanded, setExpanded] = createSignal(false)
  const file = () => props.result.file
  const shown = () => (expanded() ? props.result.matches : props.result.matches.slice(0, COLLAPSED_MATCHES))
  const hidden = () => props.result.matches.length - shown().length

  let card: HTMLDivElement | undefined
  // j/k moves the selection, and the selection has to stay on screen for that to be
  // navigation rather than guesswork
  createEffect(() => {
    if (props.selected) card?.scrollIntoView({ block: 'nearest' })
  })

  return (
    <div
      ref={card}
      class={cn('border rounded-lg bg-card', props.selected ? 'border-ring' : 'border-border')}
    >
      <div class="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs">
        <div class="i-lucide-file-code size-3.5 shrink-0 text-muted-foreground" />
        <span
          class="cursor-pointer truncate font-mono hover:underline"
          onClick={() => openFile(file().repo, file().relPath, props.result.matches[0]?.line)}
        >
          {file().relPath}
        </span>
        <div
          class="i-lucide-copy size-3 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
          title="copy path"
          onClick={() => void copy(file().relPath, 'path copied')}
        />
        <span class="shrink-0 rounded bg-muted px-1 text-muted-foreground">{file().repo}</span>
        <div class="min-w-0 flex-1" />
        <span class="shrink-0 text-muted-foreground">
          {props.result.matchCount.toLocaleString()}
          {props.result.matchCount === 1 ? ' match' : ' matches'}
          {' · '}
          {formatSize(file().size)}
        </span>
      </div>

      {/* long lines are shown whole and scroll sideways as a block, so the line
          numbers stay put and the columns of every row keep lining up */}
      <div class="overflow-x-auto py-1">
        <Index each={shown()}>
          {(match, i) => (
            <div
              class="min-w-full w-max flex cursor-pointer gap-3 bg-card py-0.25 pr-3 text-xs transition-colors hover:bg-muted/50"
              onClick={() => openFile(file().repo, file().relPath, match().line)}
            >
              {/* bg-inherit so the sticky column picks up the row's own hover colour */}
              <span class="sticky left-0 w-12 shrink-0 select-none bg-inherit pl-3 text-right text-muted-foreground font-mono">
                {match().line}
              </span>
              <span class="whitespace-pre font-mono">
                <CodeLine tokens={lineTokens(props.result.tokens, i, match().text)} />
              </span>
            </div>
          )}
        </Index>

        <Show when={hidden() > 0}>
          <Button variant="link" size="sm" class="h-6 px-3 text-xs" onClick={() => setExpanded(true)}>
            Show
            {' '}
            {hidden()}
            {hidden() === 1 ? ' more matching line' : ' more matching lines'}
          </Button>
        </Show>
      </div>
    </div>
  )
}

export function ResultsPanel() {
  const results = useSearchResults()
  const [selected, setSelected] = createSignal(0)

  const files = createMemo(() => results.data?.pages.flatMap(it => it.files) ?? [])
  const head = () => results.data?.pages[0]

  // a new query starts at the top again
  createEffect(on(() => searchQuery(), () => setSelected(0)))

  function move(delta: number) {
    const total = files().length
    if (total === 0) return
    setSelected(Math.min(total - 1, Math.max(0, selected() + delta)))
  }

  useShortcuts({
    j: () => move(1),
    k: () => move(-1),
    Enter: () => {
      const result = files()[selected()]
      if (result) openFile(result.file.repo, result.file.relPath, result.matches[0]?.line)
    },
  })

  return (
    <div class="h-full flex">
      <Show when={files().length > 0 ? head()?.facets : undefined}>
        {facets => <FacetsPanel facets={facets()} />}
      </Show>

      <div class="min-w-0 flex flex-1 flex-col">
        <Switch>
          <Match when={results.isError}>
            <div class="flex flex-1 items-center justify-center gap-2 p-2 text-xs text-destructive">
              {(results.error as Error | null)?.message}
              <Button variant="ghost" size="sm" class="h-5" onClick={() => results.refetch()}>retry</Button>
            </div>
          </Match>

          <Match when={results.isPending && searchQuery().trim()}>
            <div class="flex flex-1 items-center justify-center text-sm text-muted-foreground">searching...</div>
          </Match>

          <Match when={results.data}>
            <div class="shrink-0 border-b border-border px-3 py-1 text-xs text-muted-foreground">
              {head()!.fileTotal.toLocaleString()}
              {head()!.fileTotal === 1 ? ' file' : ' files'}
              <Show when={searchScope()}>
                {scope => (
                  <>
                    {' in '}
                    <span class="font-mono">{scope()}</span>
                    <Button variant="link" size="sm" class="h-4 px-1 text-xs" onClick={clearSearchScope}>
                      search every index
                    </Button>
                  </>
                )}
              </Show>
              <Show when={head()!.truncated && head()!.candidateTotal >= 500}>
                {' (the candidate set was capped, narrow the query)'}
              </Show>
            </div>

            <div class="min-h-0 flex-1 overflow-y-auto">
              <div class="mx-auto max-w-5xl flex flex-col gap-2 p-3">
                <For
                  each={files()}
                  fallback={<div class="py-16 text-center text-sm text-muted-foreground">nothing found</div>}
                >
                  {(result, i) => <FileResult result={result} selected={i() === selected()} />}
                </For>

                <Show when={results.hasNextPage}>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="h-8 text-xs"
                    disabled={results.isFetchingNextPage}
                    onClick={() => void results.fetchNextPage()}
                  >
                    {results.isFetchingNextPage
                      ? 'loading...'
                      : `load more (${(head()!.fileTotal - files().length).toLocaleString()} left)`}
                  </Button>
                </Show>
              </div>
            </div>
          </Match>
        </Switch>
      </div>
    </div>
  )
}
